/** Live agent status lines ("Honey: Working…") from `agent.state` events. Returns descriptors, never text. */
import type { AgentState } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";

/** Latest `agent.state` per agent (ephemeral; channelId = where the agent is working, if scoped). */
export type AgentActivity = {
  userId: string;
  name: string;
  state: AgentState;
  activity?: string;
  channelId?: string;
  at: number;
};

export type AgentStatusLine = { userId: string; state: AgentState; label: MessageDescriptor };

/** Descriptor for one agent's state, or undefined when there is nothing to show (idle / offline). */
export function agentStatusLabel(name: string, state: AgentState, activity: string | undefined): MessageDescriptor | undefined {
  switch (state) {
    case "working":
      return activity !== undefined && activity.trim() !== ""
        ? msg("agent.statusLine.activity", { name, activity: activity.trim() })
        : msg("agent.statusLine.working", { name });
    case "waiting":
      return msg("agent.statusLine.waiting", { name });
    case "error":
      return msg("agent.statusLine.error", { name });
    case "idle":
    case "offline":
      return undefined;
  }
}

const RANK: Record<AgentState, number> = { error: 0, waiting: 1, working: 2, idle: 3, offline: 4 };

/**
 * Status lines for agents active in `channelId`, most urgent first (error, waiting, working), then by name.
 * Entries without a channelId are workspace-wide and not shown under a channel's composer.
 */
export function agentStatusLines(activities: AgentActivity[], channelId: string): AgentStatusLine[] {
  const lines: { line: AgentStatusLine; name: string }[] = [];
  for (const a of activities) {
    if (a.channelId !== channelId) continue;
    const label = agentStatusLabel(a.name, a.state, a.activity);
    if (label !== undefined) lines.push({ line: { userId: a.userId, state: a.state, label }, name: a.name });
  }
  lines.sort((x, y) => {
    const r = RANK[x.line.state] - RANK[y.line.state];
    if (r !== 0) return r;
    const a = x.name.toLowerCase();
    const b = y.name.toLowerCase();
    return a < b ? -1 : a > b ? 1 : 0;
  });
  return lines.map((l) => l.line);
}
